import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, Send, Building2, User, Phone, Mail, MapPin, Clock, Lock,
  Shuffle, Ban, MessageSquare, Users, Video, AlertTriangle,
} from "lucide-react";
import { REF_STATUS_HEX, PRIORITY_HEX, pretty, hexA, fmtDate } from "./external/referralConstants";
import StaffRefDocuments from "@/components/external/StaffRefDocuments";

const StatusChip = ({ status }) => {
  const hex = REF_STATUS_HEX[status] || REF_STATUS_HEX.draft;
  return <span data-testid="ext-ref-detail-status" className="text-[12px] font-bold px-2.5 py-1 rounded-full"
    style={{ backgroundColor: hexA(hex, 0.14), color: hex }}>{pretty(status)}</span>;
};

const Contributor = ({ label, person }) => (
  <div className="text-[13px]">
    <span className="text-[#9AA5B1]">{label}: </span>
    <span className="font-medium text-[#252221]">{person?.full_name || "—"}</span>
  </div>
);

export default function StaffExternalReferralDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ statuses: [] });
  const [accounts, setAccounts] = useState([]);
  const [status, setStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [note, setNote] = useState("");
  const [noteVisible, setNoteVisible] = useState("external");
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/ext-referrals/${id}`); setR(data); }
    catch (e) { toast.error(errMsg(e, "Referral not found.")); nav("/external-referrals"); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/ext-referrals/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/external-accounts").then(({ data }) => setAccounts((data.items || []).filter((a) => a.status !== "revoked"))).catch(() => {});
  }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); await load(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const send = () => run(() => api.post(`/ext-referrals/${id}/send`), "Referral sent to the account.");
  const setStatusFn = () => { if (!status) return; run(async () => { await api.post(`/ext-referrals/${id}/status`, { status, note: statusNote || undefined }); setStatus(""); setStatusNote(""); }, "Status updated."); };
  const addNote = () => { if (!note.trim()) return; run(async () => { await api.post(`/ext-referrals/${id}/note`, { body: note, external_visible: noteVisible === "external" }); setNote(""); }, "Note added."); };
  const revoke = () => run(() => api.post(`/ext-referrals/${id}/revoke`), "External access revoked.");
  const reassign = () => { if (!reassignTo) return; run(async () => { await api.post(`/ext-referrals/${id}/reassign`, { external_account_id: reassignTo }); setReassignOpen(false); setReassignTo(""); }, "Referral reassigned."); };
  const scheduleMeeting = async () => {
    setBusy(true);
    try {
      const acc = r.external_account;
      const { data } = await api.post(`/meetings`, {
        title: `${acc?.organization || acc?.full_name || "Partner"} — ${pretty(r.referral_type)}`,
        meeting_type: "partner_planning",
        external_account_id: acc?.id, related_referral_id: id,
        external_purpose: r.summary || "Referral planning",
      });
      toast.success("Meeting created and linked to this partner.");
      nav(`/meetings/${data.meeting.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setFollowUp = (preset) => run(() => api.post(`/ext-referrals/${id}/follow-up`, { preset }), `Follow-up set for ${preset} day${preset > 1 ? "s" : ""}.`);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeMsg, setNudgeMsg] = useState("");
  const openNudge = () => { setNudgeMsg(r.suggested_followup || ""); setNudgeOpen(true); };
  const sendNudgeFollowUp = () => { if (!nudgeMsg.trim()) { toast.error("Message required."); return; } run(async () => { await api.post(`/ext-referrals/${id}/nudge`, { action: "follow_up", message: nudgeMsg }); setNudgeOpen(false); }, "Follow-up sent to partner."); };
  const nudgeDismiss = () => run(() => api.post(`/ext-referrals/${id}/nudge`, { action: "dismiss" }), "Nudge dismissed.");
  const nudgeNoResponse = () => run(() => api.post(`/ext-referrals/${id}/nudge`, { action: "no_response" }), "Marked as no response.");

  if (!r) return <div className="space-y-3"><Skeleton className="h-8 w-40" /><Skeleton className="h-32" /><Skeleton className="h-40" /></div>;

  const acc = r.external_account;
  const client = r.shared_client;
  const accLabel = (a) => `${a.organization || a.full_name} (${a.account_type_label || pretty(a.account_type)})`;

  return (
    <div className="pb-12 max-w-4xl" data-testid="staff-external-referral-detail">
      <button onClick={() => nav("/external-referrals")} className="text-[13px] text-[#4B5563] hover:text-[#1C4358] flex items-center gap-1 mb-3" data-testid="ext-ref-back">
        <ArrowLeft size={15} /> Back to External Referrals
      </button>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* PROMPT 12F — SLA acceptance nudge banner */}
          {r.sla_overdue && (
            <div className="rounded-xl border border-[#B7791F]/40 bg-[#B7791F]/8 p-4" data-testid="ext-ref-sla-nudge">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-[#B7791F] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[14px] font-semibold text-[#8a5d13]">{r.external_account?.organization || r.external_account?.full_name || "This partner"} hasn't responded yet</div>
                  <div className="text-[12px] text-[#8a5d13]/90">The expected response window has passed. Nudge them or take action — nothing is sent automatically.</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" onClick={openNudge} disabled={busy} className="bg-[#B7791F] hover:bg-[#9c661a] text-white h-8 text-[12px] gap-1" data-testid="ext-ref-nudge-followup"><Send size={13} /> Send follow-up</Button>
                <Button size="sm" variant="outline" onClick={() => setReassignOpen(true)} className="border-[#D6E0E7] text-[#1C4358] h-8 text-[12px]" data-testid="ext-ref-nudge-reassign">Reassign</Button>
                <Button size="sm" variant="outline" onClick={nudgeNoResponse} disabled={busy} className="border-[#D6E0E7] text-[#4B5563] h-8 text-[12px]" data-testid="ext-ref-nudge-noresponse">Mark no response</Button>
                <Button size="sm" variant="ghost" onClick={nudgeDismiss} disabled={busy} className="text-[#9AA5B1] h-8 text-[12px]" data-testid="ext-ref-nudge-dismiss">Dismiss</Button>
              </div>
            </div>
          )}
          <Card className="p-5 border-[#E6EDF2]">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusChip status={r.status} />
                <span className="text-[14px] font-semibold text-[#1C4358]">{pretty(r.referral_type)}</span>
                {r.priority && r.priority !== "normal" && (
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: hexA(PRIORITY_HEX[r.priority], 0.14), color: PRIORITY_HEX[r.priority] }}>{pretty(r.priority)}</span>
                )}
                {r.external_revoked && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#9C1E22]/12 text-[#9C1E22]">Access revoked</span>}
              </div>
              <div className="flex gap-2">
                {r.status === "draft" && (
                  <Button size="sm" onClick={send} disabled={busy} className="bg-[#2F7D5A] hover:bg-[#276849] text-white gap-1" data-testid="ext-ref-send-btn">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setReassignOpen(true)} className="border-[#D6E0E7] text-[#1C4358] gap-1" data-testid="ext-ref-reassign-btn"><Shuffle size={14} /> Reassign</Button>
                <Button size="sm" variant="outline" onClick={scheduleMeeting} disabled={busy} className="border-[#D6E0E7] text-[#1C4358] gap-1" data-testid="ext-ref-meeting-btn"><Video size={14} /> Schedule meeting</Button>
                {!r.external_revoked && r.status !== "draft" && (
                  <Button size="sm" variant="outline" onClick={revoke} disabled={busy} className="border-[#9C1E22]/40 text-[#9C1E22] gap-1" data-testid="ext-ref-revoke-btn"><Ban size={14} /> Revoke</Button>
                )}
              </div>
            </div>
            {r.summary && <p className="text-[15px] text-[#252221] mt-3">{r.summary}</p>}
          </Card>

          {/* Follow-up SLA */}
          <Card className="p-5 border-[#E6EDF2]" data-testid="ext-ref-followup-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[13px] font-semibold text-[#252221] flex items-center gap-1.5"><Clock size={15} /> Follow-up</div>
              {r.is_overdue && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#9C1E22]/12 text-[#9C1E22]" data-testid="ext-ref-overdue-badge">Overdue</span>}
            </div>
            <div className="text-[12px] text-[#4B5563] mt-1">
              {r.follow_up_due_at ? `Due ${fmtDate(r.follow_up_due_at)}` : "No follow-up scheduled."}
            </div>
            <div className="flex gap-2 mt-2">
              {[1, 3, 7].map((d) => (
                <Button key={d} size="sm" variant="outline" disabled={busy} onClick={() => setFollowUp(d)}
                  className="border-[#D6E0E7] text-[#1C4358] h-8 text-[12px]" data-testid={`ext-ref-followup-${d}`}>
                  In {d} day{d > 1 ? "s" : ""}
                </Button>
              ))}
            </div>
          </Card>

          {/* Set status */}
          <Card className="p-5 border-[#E6EDF2]" data-testid="ext-ref-status-panel">
            <div className="text-[13px] font-semibold text-[#252221] mb-2">Update status</div>
            <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-10" data-testid="ext-ref-status-select"><SelectValue placeholder="Choose status…" /></SelectTrigger>
                <SelectContent>{(meta.statuses || []).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={setStatusFn} disabled={busy || !status} className="bg-[#1C4358] hover:bg-[#153341] text-white gap-1 h-10" data-testid="ext-ref-status-submit">Update</Button>
            </div>
            <Textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Optional note (visible to the account)" rows={2} className="text-[14px] mt-2" data-testid="ext-ref-status-note" />
          </Card>

          {/* Notes / messages */}
          <Card className="p-5 border-[#E6EDF2]" data-testid="ext-ref-notes-panel">
            <div className="text-[13px] font-semibold text-[#252221] mb-3 flex items-center gap-1.5"><MessageSquare size={15} /> Notes & messages</div>
            <div className="space-y-2 max-h-80 overflow-y-auto mb-3">
              {(r.notes || []).length === 0 && <div className="text-[13px] text-muted-foreground">No notes yet.</div>}
              {(r.notes || []).map((n) => (
                <div key={n.id} className={`rounded-lg px-3 py-2 text-[13px] border ${
                  n.external_visible ? "bg-white border-[#E6EDF2]" : "bg-[#9C1E22]/6 border-[#9C1E22]/20"}`} data-testid={`ext-ref-note-${n.id}`}>
                  <div className="flex items-center gap-2 text-[11px] text-[#9AA5B1] mb-0.5">
                    <span className="font-semibold text-[#252221]">{n.from_external ? (r.external_org || n.by_name) : n.by_name}</span>
                    · {fmtDate(n.at)}
                    {n.external_visible
                      ? <span className="text-[#2F7D5A] font-semibold">Shared</span>
                      : <span className="text-[#9C1E22] font-semibold flex items-center gap-0.5"><Lock size={10} /> Internal</span>}
                  </div>
                  {n.body}
                </div>
              ))}
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write a note…" rows={2} className="text-[14px]" data-testid="ext-ref-note-input" />
            <div className="flex items-center justify-between gap-2 mt-2">
              <Select value={noteVisible} onValueChange={setNoteVisible}>
                <SelectTrigger className="h-9 w-[220px] text-[13px]" data-testid="ext-ref-note-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="external">Share with the account</SelectItem>
                  <SelectItem value="internal">Internal only (staff)</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={addNote} disabled={busy || !note.trim()} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1 h-9" data-testid="ext-ref-note-submit"><Send size={14} /> Add note</Button>
            </div>
          </Card>

          {/* Documents */}
          <StaffRefDocuments referralId={id} />

          {/* Timeline */}
          <Card className="p-5 border-[#E6EDF2]" data-testid="ext-ref-timeline">
            <div className="text-[13px] font-semibold text-[#252221] mb-3 flex items-center gap-1.5"><Clock size={15} /> Full timeline</div>
            <ol className="relative border-l border-[#E6EDF2] ml-1.5 space-y-4">
              {(r.timeline || []).slice().reverse().map((e, i) => (
                <li key={i} className="ml-4" data-testid="ext-ref-timeline-event">
                  <span className="absolute -left-[7px] h-3 w-3 rounded-full" style={{ backgroundColor: REF_STATUS_HEX[e.status] || "#9AA5B1" }} />
                  <div className="text-[13px] font-medium text-[#252221] flex items-center gap-1.5">
                    {e.label}
                    {e.internal && <span className="text-[10px] font-bold text-[#9C1E22] flex items-center gap-0.5"><Lock size={9} /> Internal</span>}
                    {e.account_type && <span className="text-[10px] font-semibold text-[#2C6480]">Partner</span>}
                  </div>
                  <div className="text-[11px] text-[#9AA5B1]">{e.by_name || "—"} · {fmtDate(e.at)}</div>
                  {e.note && <div className="text-[12px] text-[#4B5563] mt-0.5">{e.note}</div>}
                </li>
              ))}
            </ol>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {acc && (
            <Card className="p-5 border-[#E6EDF2]" data-testid="ext-ref-account-card">
              <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold flex items-center gap-1.5"><Building2 size={13} /> External account</div>
              <div className="font-semibold text-[#252221] mt-1.5">{acc.organization || acc.full_name}</div>
              <div className="text-[12px] text-[#4B5563]">{pretty(acc.account_type)} · {acc.full_name}</div>
              {acc.email && <a href={`mailto:${acc.email}`} className="text-[12px] text-[#4B5563] hover:text-[#1C4358] flex items-center gap-1 mt-1"><Mail size={12} /> {acc.email}</a>}
            </Card>
          )}

          {client && (client.name || client.phone || client.address) && (
            <Card className="p-5 border-[#E6EDF2]" data-testid="ext-ref-client-card">
              <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold">Client shared</div>
              <div className="mt-1.5 space-y-1 text-[13px] text-[#252221]">
                {client.name && <div className="flex items-center gap-1.5 font-medium"><User size={12} /> {client.name}</div>}
                {client.phone && <div className="flex items-center gap-1.5 text-[#4B5563]"><Phone size={12} /> {client.phone}</div>}
                {client.email && <div className="flex items-center gap-1.5 text-[#4B5563]"><Mail size={12} /> {client.email}</div>}
                {client.address && <div className="flex items-center gap-1.5 text-[#4B5563]"><MapPin size={12} /> {client.address}</div>}
              </div>
            </Card>
          )}

          <Card className="p-5 border-[#E6EDF2]" data-testid="ext-ref-contributors">
            <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold flex items-center gap-1.5 mb-2"><Users size={13} /> Contributors</div>
            <div className="space-y-1.5">
              <Contributor label="Opened by" person={r.opened_by} />
              <Contributor label="Managed by" person={r.managed_by} />
              <Contributor label="Staff contact" person={r.staff_contact} />
              <Contributor label="Closed by" person={r.closed_by} />
            </div>
          </Card>
        </div>
      </div>

      {/* SLA follow-up preview dialog */}
      <Dialog open={nudgeOpen} onOpenChange={setNudgeOpen}>
        <DialogContent data-testid="ext-ref-nudge-dialog">
          <DialogHeader>
            <DialogTitle>Send a follow-up</DialogTitle>
            <DialogDescription>Preview and edit the message before it's sent to the partner. Nothing sends automatically.</DialogDescription>
          </DialogHeader>
          <Textarea value={nudgeMsg} onChange={(e) => setNudgeMsg(e.target.value)} rows={4} className="text-[14px]" data-testid="ext-ref-nudge-message" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNudgeOpen(false)}>Cancel</Button>
            <Button onClick={sendNudgeFollowUp} disabled={busy} className="bg-[#B7791F] hover:bg-[#9c661a] text-white gap-1" data-testid="ext-ref-nudge-send">{busy && <Loader2 size={14} className="animate-spin" />} Send follow-up</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent data-testid="ext-ref-reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign referral</DialogTitle>
            <DialogDescription>Move this referral to a different external account.</DialogDescription>
          </DialogHeader>
          <Select value={reassignTo} onValueChange={setReassignTo}>
            <SelectTrigger className="h-10" data-testid="ext-ref-reassign-select"><SelectValue placeholder="Choose account…" /></SelectTrigger>
            <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{accLabel(a)}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button>
            <Button onClick={reassign} disabled={busy || !reassignTo} className="bg-[#1C4358] hover:bg-[#153341] text-white gap-1" data-testid="ext-ref-reassign-confirm">{busy && <Loader2 size={14} className="animate-spin" />} Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
